import { useEffect } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { KbSectionClausePreview } from "../molecules/KbSectionClausePreview";

export type KbSectionClausePreviewModalProps = {
  readonly open: boolean;
  readonly sectionName: string;
  readonly tag?: string | null;
  readonly description?: string | null;
  readonly content: string;
  readonly onClose: () => void;
};

export const KbSectionClausePreviewModal = ({
  open,
  sectionName,
  tag,
  description,
  content,
  onClose,
}: KbSectionClausePreviewModalProps) => {
  useEffect(() => {
    if (!open) {
      return;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [open, onClose]);

  if (!open) {
    return null;
  }

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex flex-col bg-white"
      role="dialog"
      aria-modal="true"
      aria-labelledby="kb-section-clause-preview-title"
    >
      <div className="flex shrink-0 items-start justify-between gap-3 border-b border-[var(--border)] bg-white px-4 py-3 shadow-sm">
        <div className="min-w-0">
          <h2
            id="kb-section-clause-preview-title"
            className="text-sm font-bold text-foreground"
          >
            Section clause
          </h2>
          {sectionName.trim() ? (
            <p className="mt-1 text-[11px] font-semibold text-primary">
              {sectionName}
            </p>
          ) : null}
          {tag?.trim() ? (
            <p className="mt-0.5 text-[10px] text-muted-foreground">Tag: {tag}</p>
          ) : null}
          {description?.trim() ? (
            <p className="mt-1 text-[10px] leading-snug text-muted-foreground">
              {description}
            </p>
          ) : null}
        </div>
        <button
          type="button"
          className="shrink-0 rounded p-1 text-muted-foreground hover:bg-muted"
          aria-label="Close"
          onClick={onClose}
        >
          <X className="h-4 w-4" aria-hidden />
        </button>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto bg-white px-4 py-3">
        <p className="mb-3 text-[10px] leading-snug text-muted-foreground">
          Highlighted tokens are placeholders replaced by the input fields.
        </p>
        <div className="rounded-md border border-[var(--border)] bg-slate-50 px-3 py-3">
          <KbSectionClausePreview content={content} />
        </div>
      </div>

      <div className="shrink-0 border-t border-[var(--border)] bg-white px-4 py-3">
        <button
          type="button"
          className="w-full rounded bg-violet-700 px-4 py-2.5 text-xs font-bold text-white"
          onClick={onClose}
        >
          Close
        </button>
      </div>
    </div>,
    document.body,
  );
};
